
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { sendEmailNotification } from '@/utils/emailUtils';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface RequestButtonProps {
  label: string;
  requestType: string;
  className?: string;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | null | undefined;
  additionalInfo?: Record<string, any>;
}

const RequestButton = ({ 
  label, 
  requestType, 
  className = "", 
  variant = "default",
  additionalInfo = {}
}: RequestButtonProps) => {
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Error",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      console.log(`RequestButton clicked: ${requestType}`);
      
      const enhancedInfo = {
        ...additionalInfo,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        buttonClicked: requestType
      };
      
      const result = await sendEmailNotification(requestType, enhancedInfo, email);
      
      toast({
        title: "Request Sent",
        description: "Thank you for your interest. We will contact you shortly.",
      });
      
      setIsDialogOpen(false);
      setEmail('');
      return result;
    } catch (error) {
      console.error("Request failed:", error);
      toast({
        title: "Request Failed",
        description: "Something went wrong. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <>
      <Button
        className={className}
        variant={variant}
        onClick={() => setIsDialogOpen(true)}
      >
        {label}
        <ArrowRight size={16} className="ml-2" />
      </Button>
      
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px] bg-delphi-dark border-delphi-gray/20">
          <DialogHeader>
            <DialogTitle className="text-delphi-light">Enter Your Email</DialogTitle>
            <DialogDescription className="text-delphi-light/70">
              Please provide your email address so we can send you the requested information.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-delphi-light">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-delphi-dark/50 border-delphi-gray/40 text-delphi-light"
                required
              />
            </div>
            
            <DialogFooter>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-delphi-red hover:bg-red-600 text-white"
              >
                {isSubmitting ? (
                  <span className="animate-pulse">Submitting...</span>
                ) : (
                  <>
                    Submit Request
                    <ArrowRight size={16} className="ml-2" />
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RequestButton;
